import type { Channel, LeadTemperature } from "@ai-leasing-os/shared";
import { CHANNELS } from "@ai-leasing-os/shared";
import { cn } from "@/lib/cn";
import {
  ChannelBadge,
  ChannelChip,
  CHANNEL_COLORS,
} from "@/components/badges/ChannelBadge";
import { TemperaturePill } from "@/components/badges/TemperaturePill";
import { Badge } from "@/components/ui/badge";
import { formatRelative, renderIdentityFallback } from "@/lib/format";
import { Filter, UserCog } from "lucide-react";

export interface ConversationListItem {
  id: string;
  contact_id: string;
  channel: Channel;
  status: string;
  human_takeover: number;
  takeover_reason: string | null;
  source_platform: string | null;
  display_name: string | null;
  primary_phone: string | null;
  external_thread_id: string | null;
  score: number;
  awareness_level: string;
  temperature: LeadTemperature;
  last_message_at: string | null;
}

interface Props {
  conversations: ConversationListItem[];
  isLoading?: boolean;
  selectedId: string | null;
  onSelect: (id: string) => void;
  channelFilter: string | null;
  onChannelFilter: (channel: string | null) => void;
  takeoverFilter: boolean | null;
  onTakeoverFilter: (v: boolean | null) => void;
}

export function ConversationList({
  conversations,
  isLoading = false,
  selectedId,
  onSelect,
  channelFilter,
  onChannelFilter,
  takeoverFilter,
  onTakeoverFilter,
}: Props) {
  return (
    <div>
      {/* Filters */}
      <div className="sticky top-0 bg-brand-surface border-b border-slate-200 px-3 py-2.5 space-y-2 z-10">
        <div className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-400">
          <Filter className="h-3 w-3" />
          channel
        </div>
        <div className="flex gap-1.5 flex-wrap">
          <button
            onClick={() => onChannelFilter(null)}
            className={cn(
              "inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-medium transition",
              channelFilter === null
                ? "bg-slate-900 text-white border-slate-900"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200 border-transparent",
            )}
          >
            All
          </button>
          {CHANNELS.map((ch) => (
            <ChannelChip
              key={ch}
              channel={ch}
              active={channelFilter === ch}
              onClick={() => onChannelFilter(ch === channelFilter ? null : ch)}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() =>
              onTakeoverFilter(takeoverFilter === true ? null : true)
            }
            className={cn(
              "inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-medium transition",
              takeoverFilter === true
                ? "bg-amber-500 text-white border-amber-500"
                : "bg-slate-100 text-slate-700 hover:bg-slate-200 border-transparent",
            )}
          >
            <UserCog className="h-3 w-3" />
            Takeover
          </button>
          <span className="ml-auto text-[10px] text-slate-400">
            {conversations.length} conv
          </span>
        </div>
      </div>

      {/* List */}
      {isLoading && conversations.length === 0 ? (
        <ul>
          {Array.from({ length: 6 }).map((_, i) => (
            <li
              key={i}
              className="flex items-start gap-2.5 px-3 py-3 border-b border-slate-100"
            >
              <span className="h-6 w-6 rounded bg-slate-100 animate-pulse shrink-0" />
              <div className="flex-1 min-w-0 space-y-1.5">
                <div className="h-3 w-2/3 rounded bg-slate-100 animate-pulse" />
                <div className="h-2.5 w-1/3 rounded bg-slate-100 animate-pulse" />
              </div>
            </li>
          ))}
        </ul>
      ) : conversations.length === 0 ? (
        <div className="px-3 py-12 text-center text-xs text-slate-400">
          No conversations match these filters.
        </div>
      ) : (
        <ul>
          {conversations.map((c) => {
            const isSelected = selectedId === c.id;
            const channelColor = CHANNEL_COLORS[c.channel].hex;
            return (
              <li key={c.id}>
                <button
                  onClick={() => onSelect(c.id)}
                  className={cn(
                    "relative w-full text-left px-3 py-3 border-b border-slate-100 transition-colors",
                    isSelected
                      ? "bg-slate-100"
                      : "hover:bg-slate-50",
                  )}
                >
                  {/* Left channel ribbon - 3px brand color. */}
                  <span
                    aria-hidden
                    className="absolute left-0 top-0 bottom-0 w-[3px]"
                    style={{ background: channelColor }}
                  />
                  <div className="flex items-start gap-2.5 pl-1">
                    <ChannelBadge channel={c.channel} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 mb-0.5">
                        <span className="text-sm font-medium text-slate-900 truncate flex-1">
                          {c.display_name ?? renderIdentityFallback(c)}
                        </span>
                        {c.human_takeover === 1 && (
                          <Badge variant="warning">human</Badge>
                        )}
                      </div>
                      {c.primary_phone && c.display_name && (
                        <div className="text-[10px] text-slate-400 tabular-nums truncate -mt-0.5 mb-0.5">
                          +{c.primary_phone}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-[11px] text-slate-500 flex-wrap">
                        <TemperaturePill temperature={c.temperature} />
                        <span>score {c.score}</span>
                        {c.source_platform === "marketplace" && (
                          <Badge variant="info">marketplace</Badge>
                        )}
                        <span className="ml-auto whitespace-nowrap">
                          {formatRelative(c.last_message_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
